import React from "react";
import { MdImage, MdInsertDriveFile, MdPhone, MdReply, MdVideocam } from "react-icons/md";
import { FiExternalLink } from "react-icons/fi";
import { BsListUl } from "react-icons/bs";
import dayjs from "dayjs";

import useAuthedMedia from "components/Communication/useAuthedMedia";
import WhatsAppText from "./WhatsAppText";

/**
 * A structured WhatsApp message — header (text or media), body, footer and
 * buttons — drawn the way WhatsApp draws it. Used by the template editor's
 * live preview, the lead send preview and both chat views, so a template
 * looks the same when it is written, previewed and sent.
 *
 * `layout` is the flat shape the backend returns:
 *   { header_type: NONE|TEXT|IMAGE|VIDEO|DOCUMENT, header, body, footer,
 *     buttons: [{type, text, url?, phone_number?}] }
 * `mediaUrl` is a URL the browser can load for the header file, and `media`
 * its `{filename, mime_type}`.
 */

const MEDIA_ICONS = { IMAGE: MdImage, VIDEO: MdVideocam, DOCUMENT: MdInsertDriveFile };

const BUTTON_ICONS = {
  URL: FiExternalLink,
  PHONE_NUMBER: MdPhone,
  QUICK_REPLY: MdReply,
  LIST: BsListUl,
};

const HeaderMedia = ({ type, url, media }) => {
  const Icon = MEDIA_ICONS[type];
  if (type === "DOCUMENT") {
    const name = media?.filename || "Document";
    const ext = (name.split(".").pop() || "").toUpperCase();
    return (
      <a
        href={url || undefined}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => !url && event.preventDefault()}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          background: "rgba(0,0,0,0.05)",
          borderRadius: 6,
          color: "#111b21",
          textDecoration: "none",
          marginBottom: 4,
        }}
      >
        <MdInsertDriveFile size={28} color="#e53935" style={{ flexShrink: 0 }} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </div>
          {ext && <div style={{ fontSize: 11, color: "#8696a0" }}>{ext}</div>}
        </div>
      </a>
    );
  }
  if (url && type === "IMAGE")
    return (
      <img
        src={url}
        alt={media?.filename || "Header"}
        style={{
          display: "block",
          width: "100%",
          maxHeight: 260,
          objectFit: "cover",
          borderRadius: 6,
          marginBottom: 4,
        }}
      />
    );
  if (url && type === "VIDEO")
    return (
      <video
        controls
        preload="metadata"
        src={url}
        style={{ display: "block", width: "100%", maxHeight: 260, borderRadius: 6, marginBottom: 4 }}
      />
    );
  return (
    <div
      style={{
        height: 140,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#cfd8dc",
        borderRadius: 6,
        color: "#fff",
        marginBottom: 4,
      }}
    >
      {Icon ? <Icon size={48} /> : null}
    </div>
  );
};

export const WhatsAppButtons = ({ buttons }) =>
  buttons?.length ? (
    <div style={{ marginTop: 6, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
      {buttons.map((button, index) => {
        const Icon = BUTTON_ICONS[String(button.type || "").toUpperCase()] || MdReply;
        const content = (
          <>
            <Icon size={14} style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {button.text || "Button"}
            </span>
          </>
        );
        const style = {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          color: "#027eb5",
          fontSize: 13.5,
          fontWeight: 500,
          padding: "7px 4px",
          textDecoration: "none",
          borderTop: index ? "1px solid rgba(0,0,0,0.06)" : "none",
        };
        return button.url ? (
          <a key={index} href={button.url} target="_blank" rel="noopener noreferrer" style={style}>
            {content}
          </a>
        ) : (
          <div key={index} style={style}>
            {content}
          </div>
        );
      })}
    </div>
  ) : null;

const WhatsAppLayout = ({ layout, mediaUrl, media, children }) => {
  if (!layout) return null;
  const headerType = String(layout.header_type || "NONE").toUpperCase();
  return (
    <div>
      {headerType === "TEXT" && layout.header && (
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 3, color: "#111b21" }}>
          <WhatsAppText text={layout.header} />
        </div>
      )}
      {MEDIA_ICONS[headerType] && (
        <HeaderMedia type={headerType} url={mediaUrl || layout.header_media_link} media={media} />
      )}
      {layout.body && (
        <div style={{ fontSize: 13.5, lineHeight: 1.45, color: "#111b21" }}>
          <WhatsAppText text={layout.body} />
        </div>
      )}
      {layout.footer && (
        <div style={{ color: "#8696a0", fontSize: 12, marginTop: 4 }}>{layout.footer}</div>
      )}
      {children}
      <WhatsAppButtons buttons={layout.buttons} />
    </div>
  );
};

/**
 * The layout inside a phone-style chat background, for editors and previews
 * where there is no thread around it.
 */
export const WhatsAppPreview = ({ layout, mediaUrl, media, title = "Preview" }) => (
  <div
    style={{
      background: "#efeae2",
      borderRadius: 8,
      padding: 12,
      minHeight: 160,
    }}
  >
    <div style={{ fontSize: 11.5, color: "#54656f", textAlign: "center", marginBottom: 8 }}>
      {title}
    </div>
    <div
      style={{
        background: "#fff",
        borderRadius: "0 8px 8px 8px",
        padding: "6px 8px 4px",
        maxWidth: 340,
        boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
      }}
    >
      {layout && (layout.body || layout.header || (layout.header_type && layout.header_type !== "NONE")) ? (
        <WhatsAppLayout layout={layout} mediaUrl={mediaUrl} media={media}>
          <div style={{ textAlign: "right", fontSize: 10.5, color: "#8696a0", marginTop: 2 }}>
            {dayjs().format("HH:mm")}
          </div>
        </WhatsAppLayout>
      ) : (
        <div style={{ color: "#8696a0", fontSize: 13, padding: "8px 0" }}>
          Start typing to see the message.
        </div>
      )}
    </div>
  </div>
);


/**
 * A preview straight from the render endpoints (template preview, send
 * preview), whose `header_media.url` is behind auth.
 */
export const RenderedWhatsAppPreview = ({ rendered, title }) => {
  const { url } = useAuthedMedia(rendered?.header_media?.url);
  return (
    <WhatsAppPreview
      layout={rendered}
      mediaUrl={url}
      media={rendered?.header_media}
      title={title}
    />
  );
};

export default WhatsAppLayout;
